// Pure helpers for the admin "Therapist Performance" widget.
//
// Input shape (from GET /admin/therapist-performance): each therapist has
// `goal` (weekly session goal), `bonus` (weekly bonus threshold) and `weeks`,
// oldest → newest, where the LAST entry is the current, still-open week.
// Each week: { weekStart, scheduled, canceled, billed, sessions, noShows, cancels }.
//   scheduled / canceled — Acuity appointments (kept / canceled)
//   billed               — everything the therapist submitted and collected on,
//                          INCLUDING no-show and cancellation fees (Eric's call:
//                          what they collected is what counts)
//   sessions / noShows / cancels — the breakdown of `billed`
//
// Status is judged on COMPLETED weeks only, so a therapist mid-week never reads
// as behind because the week isn't over.

export const STATUS = {
  BEHIND: 'behind',
  WATCH: 'watch',
  ON_TRACK: 'on_track',
  NO_GOAL: 'no_goal',
};

export const STATUS_META = {
  [STATUS.BEHIND]: { label: 'Behind', icon: '▼', color: 'var(--error-color)', bg: '#fde8e8', rank: 0 },
  [STATUS.WATCH]: { label: 'Watch', icon: '●', color: '#B45309', bg: '#fff3cd', rank: 1 },
  [STATUS.ON_TRACK]: { label: 'On track', icon: '✓', color: 'var(--success-color)', bg: '#e7f6ec', rank: 2 },
  [STATUS.NO_GOAL]: { label: 'No goal set', icon: '–', color: 'var(--text-muted)', bg: 'var(--surface-muted)', rank: 3 },
};

const WINDOW = 4;          // weeks in the judging window
const TREND_BAND = 0.10;   // ±10% reads as flat
const UNBILLED_FLAG_AT = 3;

const sum = (arr, key) => arr.reduce((acc, w) => acc + (Number(w[key]) || 0), 0);
const avg = (arr, key) => (arr.length ? sum(arr, key) / arr.length : 0);

function trendOf(recent, prior, hasPrior) {
  if (!hasPrior) return 'flat';
  if (prior === 0) return recent > 0 ? 'up' : 'flat';
  const ratio = (recent - prior) / prior;
  if (ratio > TREND_BAND) return 'up';
  if (ratio < -TREND_BAND) return 'down';
  return 'flat';
}

export function summarizeTherapist(t) {
  const weeks = Array.isArray(t.weeks) ? t.weeks : [];
  const completed = weeks.slice(0, Math.max(0, weeks.length - 1));
  const current = weeks.length ? weeks[weeks.length - 1] : null;
  const last4 = completed.slice(-WINDOW);
  const prior4 = completed.slice(-WINDOW * 2, -WINDOW);
  const hasPrior = prior4.length === WINDOW;

  const goal = Number(t.goal) || 0;
  const bonus = Number(t.bonus) || 0;
  const avg4 = avg(last4, 'billed');
  const prevAvg4 = avg(prior4, 'billed');

  let status = STATUS.NO_GOAL;
  if (goal > 0) {
    if (avg4 >= goal) status = STATUS.ON_TRACK;
    else if (avg4 >= goal * 0.75) status = STATUS.WATCH;
    else status = STATUS.BEHIND;
  }

  const lastWeek = completed[completed.length - 1] || null;
  const bonusLastWeek = !!(bonus > 0 && lastWeek && Number(lastWeek.billed) >= bonus);

  const unbilledGap = last4.reduce(
    (acc, w) => acc + Math.max(0, (Number(w.scheduled) || 0) - (Number(w.billed) || 0)), 0
  );

  const noShows4 = sum(last4, 'noShows');
  const noShowsPrior4 = sum(prior4, 'noShows');

  const kept4 = sum(last4, 'scheduled');
  const canceled4 = sum(last4, 'canceled');
  const cancelRate = kept4 + canceled4 > 0 ? canceled4 / (kept4 + canceled4) : null;

  return {
    status,
    goal,
    bonus,
    avg4,
    prevAvg4,
    trend: trendOf(avg4, prevAvg4, hasPrior),
    bonusLastWeek,
    unbilledGap,
    unbilledFlag: unbilledGap >= UNBILLED_FLAG_AT,
    noShows4,
    noShowsPrior4,
    noShowTrend: trendOf(noShows4, noShowsPrior4, hasPrior),
    cancelRate,
    current,
    lastWeek,
    completedCount: completed.length,
  };
}

// Attention first: behind, watch, on track, no goal; ties by last name.
export function sortForAttention(list) {
  return [...list].sort((a, b) => {
    const ra = STATUS_META[a.summary.status].rank;
    const rb = STATUS_META[b.summary.status].rank;
    if (ra !== rb) return ra - rb;
    return `${a.lastName || ''} ${a.firstName || ''}`.localeCompare(`${b.lastName || ''} ${b.firstName || ''}`);
  });
}
